import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';
import { AuthExperience } from './AuthExperience';
import {
  FluentProvider,
  makeStyles,
  tokens,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from '@fluentui/react-components';
import type { AuthContextType, TeamsThemeName, UserInfo } from '../../types';
import { api, ApiError } from '../../services/api';
import { toggleBuildInfoVisibility, useBuildInfoVisibility } from '../../utils/buildInfo';

// Exported (not just useAuth) so tests can render a component tree under a
// mock auth value without standing up the full provider -- see
// e2e/pageheader-role-gating.spec.tsx.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token refresh interval (refresh 5 minutes before typical 1-hour expiry)
const TOKEN_REFRESH_INTERVAL = 55 * 60 * 1000; // 55 minutes
const EMULATION_STORAGE_KEY = 'voiceiq_platform_emulation';

const BROWSER_RESOURCE_BY_HOST: Record<string, string> = {
  'logicv-teams-cdr.azurewebsites.net': 'api://logicv-teams-cdr.azurewebsites.net/82a7d8cd-a1ac-4aa3-84e2-dab7a4768faf',
  'app.voiceiq.logicv.com': 'api://logicv-teams-cdr.azurewebsites.net/82a7d8cd-a1ac-4aa3-84e2-dab7a4768faf',
  'logicv-teams-cdr-dev.azurewebsites.net': 'api://logicv-teams-cdr-dev.azurewebsites.net/82a7d8cd-a1ac-4aa3-84e2-dab7a4768faf',
  'dev-app.voiceiq.logicv.com': 'api://logicv-teams-cdr-dev.azurewebsites.net/82a7d8cd-a1ac-4aa3-84e2-dab7a4768faf',
};

function getFluentTheme(themeName: TeamsThemeName) {
  switch (themeName) {
    case 'dark':
      return teamsDarkTheme;
    case 'contrast':
      return teamsHighContrastTheme;
    default:
      return teamsLightTheme;
  }
}

function isStandaloneBrowserTeamsError(message: string) {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('sdk initialization failed') ||
    normalized.includes('no parent window found') ||
    normalized.includes('initialization failed') ||
    normalized.includes('not running in teams')
  );
}

const useStyles = makeStyles({
  version: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '8px',
    color: 'transparent',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    backgroundColor: 'transparent',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    zIndex: 1000,
    transitionProperty: 'width, background-color, color, box-shadow',
    transitionDuration: '180ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    ':hover': {
      width: 'auto',
      color: tokens.colorNeutralForeground2,
      backgroundColor: tokens.colorNeutralBackground1,
      borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      paddingLeft: '12px',
      paddingRight: '12px',
      boxShadow: tokens.shadow8,
    },
  },
});

interface TeamsAuthProviderProps {
  children: React.ReactNode;
}

type EmulationContext = {
  customerAccountId: string;
  userEmail: string;
};

function readStoredEmulation(): EmulationContext | null {
  try {
    const raw = window.localStorage.getItem(EMULATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<EmulationContext>;
    if (!parsed.customerAccountId || !parsed.userEmail) {
      return null;
    }

    return {
      customerAccountId: parsed.customerAccountId,
      userEmail: parsed.userEmail,
    };
  } catch {
    return null;
  }
}

export function TeamsAuthProvider({ children }: TeamsAuthProviderProps) {
  const styles = useStyles();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('Starting...');
  const [themeName, setThemeName] = useState<TeamsThemeName>('default');
  const [isInTeams, setIsInTeams] = useState(false);
  // Mirror of isInTeams for code that runs outside the render cycle. The
  // refresh timer and the API client's 401 handler are registered during
  // initializeTeams(), i.e. from the render where isInTeams was still false,
  // and init only ever runs once -- so anything that reads the *state* from
  // those closures is frozen at false and takes the browser/MSAL path
  // inside Teams. Read the ref instead.
  const isInTeamsRef = useRef(false);
  const [buildInfoHint, setBuildInfoHint] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buildInfoHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The in-flight refresh, shared so a 401 retry can await the real refresh
  // rather than a fixed sleep, and concurrent triggers coalesce.
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const msalInstanceRef = useRef<PublicClientApplication | null>(null);
  const msalInitPromiseRef = useRef<Promise<PublicClientApplication> | null>(null);
  const initStartedRef = useRef(false);
  const [emulationContext, setEmulationContext] = useState<EmulationContext | null>(() =>
    readStoredEmulation()
  );

  const markInTeams = useCallback((value: boolean) => {
    isInTeamsRef.current = value;
    setIsInTeams(value);
  }, []);

  const getBrowserResource = useCallback(() => {
    const host = window.location.host.toLowerCase();
    return BROWSER_RESOURCE_BY_HOST[host] || null;
  }, []);

  const getBrowserRedirectUri = useCallback(() => {
    return `${window.location.origin}/auth-end`;
  }, []);

  const getMsalInstance = useCallback(() => {
    if (!msalInstanceRef.current) {
      const config: Configuration = {
        auth: {
          clientId: __AZURE_CLIENT_ID__,
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: getBrowserRedirectUri(),
        },
        cache: {
          cacheLocation: 'localStorage',
        },
      };
      msalInstanceRef.current = new PublicClientApplication(config);
    }
    return msalInstanceRef.current;
  }, [getBrowserRedirectUri]);

  const ensureMsalInitialized = useCallback(async () => {
    if (!msalInitPromiseRef.current) {
      const instance = getMsalInstance();
      msalInitPromiseRef.current = instance.initialize().then(() => instance);
    }
    return msalInitPromiseRef.current;
  }, [getMsalInstance]);

  const buildBrowserTokenRequest = useCallback((account: AccountInfo): SilentRequest | null => {
    const resource = getBrowserResource();
    if (!resource) {
      return null;
    }
    return {
      account,
      scopes: [`${resource}/access_as_user`],
    };
  }, [getBrowserResource]);

  // Function to refresh the token
  const refreshToken = useCallback((): Promise<void> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current; // Already refreshing -- join it
    }

    const run = async () => {
      try {
        let newToken: string;

        if (isInTeamsRef.current) {
          console.log('Refreshing Teams auth token...');
          newToken = await microsoftTeams.authentication.getAuthToken();
        } else {
          console.log('Refreshing browser auth token...');
          const msalInstance = await ensureMsalInitialized();
          const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
          if (!account) {
            throw new Error('No browser sign-in session found');
          }

          const silentRequest = buildBrowserTokenRequest(account);
          if (!silentRequest) {
            throw new Error('Browser sign-in is not configured for this host');
          }

          const result = await msalInstance.acquireTokenSilent(silentRequest);
          newToken = result.accessToken;
        }

        setToken(newToken);
        api.setToken(newToken);
        console.log('Token refreshed successfully');
      } catch (err) {
        console.error('Token refresh failed:', err);
        // Don't set error state - the user can continue using the app
        // until the next API call fails
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = run();
    return refreshPromiseRef.current;
  }, [buildBrowserTokenRequest, ensureMsalInitialized]);

  // Latest-function ref: the refresh timer and the API client's 401 handler
  // are long-lived and must always call the current refreshToken, never the
  // one captured when they were registered.
  const refreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  const applyAuthenticatedSession = useCallback(async (newToken: string) => {
    setToken(newToken);
    api.setToken(newToken);

    api.setOnUnauthorized(() => refreshTokenRef.current());

    let userInfo: UserInfo;
    try {
      userInfo = await api.getCurrentUser();
    } catch (err) {
      // A stale emulation context (target no longer a member -> 404, or the
      // actor lost tenant_admin -> 403) would otherwise brick the app: every
      // request including /auth/me fails, this throws before setUser runs,
      // and the Exit-emulation button never renders -- only clearing
      // localStorage by hand recovers. Clear the stored context and retry
      // /auth/me exactly once instead.
      const isStaleEmulationError =
        err instanceof ApiError &&
        (err.status === 403 || err.status === 404) &&
        emulationContext !== null;

      if (!isStaleEmulationError) {
        throw err;
      }

      window.localStorage.removeItem(EMULATION_STORAGE_KEY);
      setEmulationContext(null);
      api.setEmulationContext(null);
      userInfo = await api.getCurrentUser();
    }

    if (emulationContext && !userInfo.is_emulating) {
      window.localStorage.removeItem(EMULATION_STORAGE_KEY);
      setEmulationContext(null);
      api.setEmulationContext(null);
    }
    setUser(userInfo);
    setError(null);
    return userInfo;
  }, [emulationContext]);

  // Set up automatic token refresh
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Schedule refresh before token expires
    refreshTimerRef.current = setTimeout(() => {
      void refreshTokenRef.current();
      // Schedule next refresh
      scheduleTokenRefresh();
    }, TOKEN_REFRESH_INTERVAL);
  }, []);

  const initializeTeams = useCallback(async (): Promise<boolean> => {
    try {
      setDebugInfo('Initializing Teams SDK...');

      // Initialize Teams SDK
      await microsoftTeams.app.initialize();
      markInTeams(true);
      setDebugInfo('Teams SDK initialized. Getting context...');

      // Get Teams context
      const context = await microsoftTeams.app.getContext();
      setDebugInfo(`Context received. User: ${context.user?.userPrincipalName || 'unknown'}`);

      // Detect Teams theme
      const initialTheme = (context.app?.theme as TeamsThemeName) || 'default';
      setThemeName(initialTheme);

      // Listen for theme changes (e.g., user toggles dark mode in Teams)
      microsoftTeams.app.registerOnThemeChangeHandler((theme: string) => {
        setThemeName((theme as TeamsThemeName) || 'default');
      });

      // Notify Teams that the app is loaded
      microsoftTeams.app.notifySuccess();

      // Get auth token from Teams
      setDebugInfo('Requesting auth token...');
      const authToken = await microsoftTeams.authentication.getAuthToken();
      setDebugInfo('Auth token received. Fetching user info...');
      await applyAuthenticatedSession(authToken);
      setDebugInfo('Authenticated successfully!');

      // Start automatic token refresh
      scheduleTokenRefresh();
      return true;

    } catch (err: unknown) {
      console.error('Teams auth error:', err);

      const errorMessage = err instanceof Error ? err.message : String(err);

      // Check error type
      if (errorMessage.includes('resourceDisabled') || errorMessage.includes('invalid_resource')) {
        setError('Azure AD App not configured correctly. Check Application ID URI matches: api://localhost:3000/82a7d8cd-a1ac-4aa3-84e2-dab7a4768faf');
      } else if (errorMessage.includes('AADSTS65001')) {
        setError('Admin consent required. Have your admin consent to the app permissions.');
      } else if (isStandaloneBrowserTeamsError(errorMessage)) {
        markInTeams(false);
        setError(null);
        setDebugInfo('Standalone browser access detected.');
        return false;
      } else {
        setError(errorMessage);
      }

      // Still notify Teams that something loaded (to prevent blank screen)
      if (!isStandaloneBrowserTeamsError(errorMessage)) {
        try {
          microsoftTeams.app.notifySuccess();
        } catch {
          // Ignore if this fails
        }
      }
      return true;
    } finally {
      setIsLoading(false);
    }
  }, [applyAuthenticatedSession, markInTeams, scheduleTokenRefresh]);

  const initializeBrowserAuth = useCallback(async () => {
    setDebugInfo('Initializing browser sign-in...');

    const resource = getBrowserResource();
    if (!resource) {
      setError('Browser sign-in is only configured for the approved app hosts.');
      setIsLoading(false);
      return;
    }

    try {
      const msalInstance = await ensureMsalInitialized();
      const redirectResponse = await msalInstance.handleRedirectPromise();

      let account = redirectResponse?.account || msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
      if (account) {
        msalInstance.setActiveAccount(account);
      }

      if (!account) {
        setDebugInfo('Browser sign-in ready.');
        setError(null);
        setIsLoading(false);
        return;
      }

      const tokenRequest = buildBrowserTokenRequest(account);
      if (!tokenRequest) {
        throw new Error('Browser sign-in is not configured for this host');
      }

      setDebugInfo('Acquiring browser access token...');
      let authResult: AuthenticationResult;
      try {
        authResult = await msalInstance.acquireTokenSilent(tokenRequest);
      } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) {
          throw error;
        }
        setDebugInfo('Additional browser consent is required.');
        setError(null);
        setIsLoading(false);
        return;
      }

      account = authResult.account || account;
      msalInstance.setActiveAccount(account);
      setDebugInfo('Browser token received. Fetching user info...');
      await applyAuthenticatedSession(authResult.accessToken);
      scheduleTokenRefresh();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('Browser auth error:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [applyAuthenticatedSession, buildBrowserTokenRequest, ensureMsalInitialized, getBrowserResource, scheduleTokenRefresh]);

  const signInWithBrowser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const resource = getBrowserResource();
      if (!resource) {
        throw new Error('Browser sign-in is not configured for this host');
      }

      const msalInstance = await ensureMsalInitialized();
      const request: RedirectRequest = {
        scopes: [`${resource}/access_as_user`],
        redirectStartPage: window.location.href,
      };
      await msalInstance.loginRedirect(request);
    } catch (err: unknown) {
      if (err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress') {
        return;
      }
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('Browser login failed:', err);
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [ensureMsalInitialized, getBrowserResource]);

  const startEmulation = useCallback(async (customerAccountId: string, userEmail: string) => {
    const nextContext = {
      customerAccountId,
      userEmail: userEmail.trim().toLowerCase(),
    };

    api.setEmulationContext(nextContext);

    try {
      const userInfo = await api.startEmulation(
        nextContext.customerAccountId,
        nextContext.userEmail
      );
      window.localStorage.setItem(EMULATION_STORAGE_KEY, JSON.stringify(nextContext));
      setEmulationContext(nextContext);
      setUser(userInfo);
      setError(null);
      return userInfo;
    } catch (err) {
      api.setEmulationContext(emulationContext);
      throw err;
    }
  }, [emulationContext]);

  const clearEmulation = useCallback(async () => {
    window.localStorage.removeItem(EMULATION_STORAGE_KEY);
    setEmulationContext(null);
    api.setEmulationContext(null);
    const userInfo = await api.getCurrentUser();
    setUser(userInfo);
    setError(null);
    return userInfo;
  }, []);

  useEffect(() => {
    api.setEmulationContext(emulationContext);
  }, [emulationContext]);

  useEffect(() => {
    if (initStartedRef.current) {
      return;
    }
    initStartedRef.current = true;

    let isMounted = true;

    async function initializeAuth() {
      const runningInTeams = await initializeTeams();
      if (isMounted && !runningInTeams && !user && !token) {
        await initializeBrowserAuth();
      }
    }

    void initializeAuth();

    // Cleanup timer on unmount
    return () => {
      isMounted = false;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [initializeBrowserAuth, initializeTeams, isInTeams, token, user]);

  // Always render FluentProvider with the detected theme
  useEffect(() => {
    if (!isInTeams || user?.is_internal !== true) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'b')) {
        return;
      }

      event.preventDefault();
      const visible = toggleBuildInfoVisibility();

      if (buildInfoHintTimerRef.current) {
        clearTimeout(buildInfoHintTimerRef.current);
      }

      setBuildInfoHint(visible ? 'Build info shown' : 'Build info hidden');
      buildInfoHintTimerRef.current = setTimeout(() => {
        setBuildInfoHint(null);
      }, 3000);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (buildInfoHintTimerRef.current) {
        clearTimeout(buildInfoHintTimerRef.current);
      }
    };
  }, [isInTeams, user?.is_internal]);

  const fluentTheme = getFluentTheme(themeName);
  const showBuildInfo = useBuildInfoVisibility();
  const shortSourceSha = __SOURCE_SHA__ && __SOURCE_SHA__ !== 'unknown'
    ? __SOURCE_SHA__.slice(0, 7)
    : '';
  const buildLabel = __BUILD_ID__
    ? `v${__APP_VERSION__} (${__BUILD_ID__}${shortSourceSha ? ` · ${shortSourceSha}` : ''})${isInTeams ? ' · Teams' : ' · Browser'}`
    : `v${__APP_VERSION__}${isInTeams ? ' · Teams' : ' · Browser'}`;
  const authPreviewMode = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get('auth-preview')
    : null;

  if (authPreviewMode === 'loading' || authPreviewMode === 'sign-in' || authPreviewMode === 'error') {
    return (
      <FluentProvider theme={fluentTheme} style={{ backgroundColor: '#071321', minHeight: '100vh' }}>
        <AuthExperience
          mode={authPreviewMode}
          status="Requesting Teams auth token…"
          error={authPreviewMode === 'error' ? 'Microsoft sign-in was interrupted. Your account was not changed.' : null}
          onSignIn={() => undefined}
          onRetry={() => undefined}
        />
      </FluentProvider>
    );
  }

  if (isLoading) {
    return (
      <FluentProvider theme={fluentTheme} style={{ backgroundColor: '#071321', minHeight: '100vh' }}>
        <AuthExperience mode="loading" status={debugInfo} />
        {showBuildInfo && <div className={styles.version}>{buildLabel}</div>}
        {buildInfoHint && <div className={styles.version} style={{ top: '56px' }}>{buildInfoHint}</div>}
      </FluentProvider>
    );
  }

  if (!isInTeams && !(user && token)) {
    return (
      <FluentProvider theme={fluentTheme} style={{ backgroundColor: '#071321', minHeight: '100vh' }}>
        <AuthExperience
          mode={error ? 'error' : 'sign-in'}
          error={error}
          onSignIn={() => void signInWithBrowser()}
          onRetry={() => void signInWithBrowser()}
        />
        {showBuildInfo && <div className={styles.version}>{buildLabel}</div>}
        {buildInfoHint && <div className={styles.version} style={{ top: '56px' }}>{buildInfoHint}</div>}
      </FluentProvider>
    );
  }

  if (error && !user) {
    return (
      <FluentProvider theme={fluentTheme} style={{ backgroundColor: '#071321', minHeight: '100vh' }}>
        <AuthExperience mode="error" error={error} onRetry={() => window.location.reload()} />
        {showBuildInfo && <div className={styles.version}>{buildLabel}</div>}
        {buildInfoHint && <div className={styles.version} style={{ top: '56px' }}>{buildInfoHint}</div>}
      </FluentProvider>
    );
  }

  const contextValue: AuthContextType = {
    user,
    token,
    isLoading,
    error,
    isAuthenticated: !!user && !!token,
    themeName,
    isInTeams,
    startEmulation,
    clearEmulation,
  };

  return (
    <FluentProvider theme={fluentTheme} style={{ backgroundColor: 'var(--colorNeutralBackground2)', minHeight: '100vh' }}>
      {showBuildInfo && <div className={styles.version}>{buildLabel}</div>}
      {buildInfoHint && <div className={styles.version} style={{ top: '56px' }}>{buildInfoHint}</div>}
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </FluentProvider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a TeamsAuthProvider');
  }
  return context;
}
